import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {  leadForm } from "../../../formSchemas/signup.form";
import DynamicForms from "../../../yoello-lib/modules/dynamicForms/DynamicForms";
import EColourPanel from "../../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import { useSignup } from "../state/SelfSignup";
import { setLeadData, setStage } from "../state/signup.action";
import { Acquirers, FeePlans, ILead, OnboardingSources } from "../types";
import { ChevronRight } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core";
import { leadStages } from "../state/LeadStages";
import { hosts } from "../../../yoello-lib/modules/host/Hosts";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../../reducers";
import {COUNTRIES} from "../../../const/countries.const"
import HostClient from "../../../yoello-lib/modules/host/HostClient";
import { getCurrencySymbolForLeadSource } from "../../../utils/getCurrencySymbolForLeadSource";
import { handleIntegrationMetaData } from "./VenueDetailsForm";

 
const useStyles = makeStyles({
    venueLocationPosition: {
        marginBottom: "0.75rem"
    },
    TermsOfService: {
        display: "flex",
        justifyContent:"flex-end",
        "& p": {
            margin: 0,
            marginBottom: "0.6875rem"
        }
    },
    submitButton: {
        position: "absolute",
        bottom: "1.25rem",
        right: "3.75rem",
        width: "unset!important",
        padding: "0.875rem 1.5625rem 0.875rem 2.25rem;"
    },
    IconLabel: {
        "& label": {
            display: "flex",
            alignItems: "center",
            "& span": {
                height: "100%",
                display: "flex"
            }

        }
    },
    venueNameRoot:{
        "& .MuiFormHelperText-contained":{
            marginLeft:"0 !important"
        }
    }
    
});

const leadButtons: Record<string, string> = {
    [hosts.rms]: 'misc.continue',
    [hosts.cardsaver]: 'misc.continue'
}

export function LeadForm() {
    const { t } = useTranslation();
    const host = HostClient.host;
    const { dispatch, leadData, emailLookup, callLeadsAPI, getTransactionFeePlans } = useSignup();
    const [isLoading, setIsLoading] = useState(false);
    const [feePlans, setFeePlans] = useState<FeePlans[]|undefined>(undefined);
    const classNames = useStyles()
    const timeout = useRef<null | NodeJS.Timeout>(null);

    const isZeroBubbleLead = host === hosts.zerobubble;
    const isBNALead = host === hosts.bna;
    const isZonalLead = host === hosts.zonal;

    const venueID = useSelector((state: IReduxAppState) => state.selectedVenueId);

    const VenueLocation = useSelector(
      (state: IReduxAppState) => state.venue[venueID]?.venueLocation
    );

    const venueData = useSelector(
    (state: IReduxAppState) => state.venue[venueID]?.venueData
    );

    useEffect(() => {
       if (isZeroBubbleLead) {
        getTransactionFeePlans(hosts.zerobubble).then((data) => setFeePlans(data))
       }
       if (isBNALead) {
        getTransactionFeePlans(hosts.bna).then((data) => setFeePlans(data))
       }
       if (isZonalLead) {
        getTransactionFeePlans(hosts.zonal).then((data) => setFeePlans(data))
       }
    // eslint-disable-next-line
    }, [isZeroBubbleLead, isBNALead, isZonalLead, host]);

    const formRows = useMemo(() => {
        const rows = leadForm(t, emailLookup, classNames , host , feePlans);
        if (host !== hosts.barclaycard) {
            rows.splice(rows.length - 1);
        }

        if (isZeroBubbleLead || isBNALead) {
            rows.splice(0,3);
        }

        return rows;
        //eslint-disable-next-line
    }, [host, feePlans, isZeroBubbleLead, isBNALead])

    const buttonLabel = leadButtons[host] ?? "signup.form.submitButton";

    const getLeadVenueLocationCountry = useCallback((host: string) => {
        switch (host) {
            case hosts.zerobubble:
                return { name: "Malta", value: "MT"};
            case hosts.bna:
                return { name: "Canada", value: "CA"};
            default:
                return {
                    name: COUNTRIES.find(item => item.code === VenueLocation?.address_country)?.name ?? "United Kingdom",
                    value: VenueLocation?.address_country ?? "GB"
                }
        }
    }, [VenueLocation])

    // If partner is BNA or Zonal we select the first fee plan since they only have one fee plan anyways
    const shouldSelectFirstFeePlan = isBNALead || isZonalLead;
    const initialLeadFormDefaultData = useMemo(() => {
        if (host) {
            const defaultData = {
                venue_location: getLeadVenueLocationCountry(host),
            };
            if (shouldSelectFirstFeePlan && feePlans) {
                const defaultPlan = feePlans[0];
                return {
                    ...defaultData,
                    processing_fee_plan_id: { 
                        name: `${defaultPlan.order_processing_charge_percentage}% + ${getCurrencySymbolForLeadSource(host)}${(defaultPlan.order_processing_charge / 100).toFixed(2)}`,
                        value: defaultPlan.id}
                }
            }
            return defaultData;
        } 
        return {};
    }, [host, feePlans, getLeadVenueLocationCountry, shouldSelectFirstFeePlan]);

    async function onSubmit(data: any) {
        let lead = data as ILead;
        if (data && data.processing_fee_plan_id) {
            lead.processing_fee_plan_id = data.processing_fee_plan_id.value;
        }
        const acquirerName = host === hosts.worldpay ? Acquirers.Worldpay :  host === hosts.barclaycard ? Acquirers.barclaycard : undefined; 

        dispatch(setLeadData(lead));
        setIsLoading(true);
        const finalLeadData = handleIntegrationMetaData(lead, host.toUpperCase())
        await callLeadsAPI({
            acquirer_name: acquirerName,
            ...finalLeadData,
            onboarding_source: OnboardingSources[host],
            application_id:venueData?.application_id
        });
        setIsLoading(false);
        dispatch(setStage(leadStages[host]));
    }

    useEffect(() => {
        /**Form doesn't update properly if the query params */
        if (!leadData) {
            const id = setTimeout(() => {
                dispatch(setLeadData({}));
            }, 25);
            timeout.current = id;
        } else {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        }
        //eslint-disable-next-line
    }, [leadData]);

    return <DynamicForms
        onHandleSubmit={onSubmit}
        formKeyName="register-bc-details"
        formRows={formRows}
        formData={initialLeadFormDefaultData}
        formButtons={{
            rightButton: {
                type: 'submit', label: buttonLabel,
                color: EColourPanel.PRIMARY, disabled: isLoading,
                className: classNames.submitButton, endIcon: <ChevronRight />,
            }
        }}
        useFormOptions={
            {
                reValidateMode: 'onBlur',
                mode: 'onBlur'
            }
        }
    />
}
